import { useState, useCallback, MouseEvent } from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CheckIcon from "@mui/icons-material/Check";
import type { OpportunitySubcategory } from "@api/opportunity";

type Props = {
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  subcategories: OpportunitySubcategory[];
};

export const SubcategorySelector = ({ value, onChange, disabled, subcategories }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleSelect = useCallback(
    (id: string) => {
      onChange(id);
      setAnchorEl(null);
    },
    [onChange]
  );

  const selectedName = subcategories.find((s) => s.id === value)?.name;
  const tooltip = selectedName
    ? `Subcategory: ${selectedName}`
    : "Select subcategory (required)";

  return (
    <>
      <Tooltip title={tooltip}>
        <span>
          <IconButton
            size="small"
            onClick={handleOpen}
            disabled={disabled}
            color={value ? "default" : "warning"}
            aria-label={tooltip}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <LocalOfferIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {subcategories.map((sub) => {
          const selected = sub.id === value;
          return (
            <MenuItem
              key={sub.id}
              selected={selected}
              onClick={() => handleSelect(sub.id)}
              dense
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {selected && <CheckIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>{sub.name}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
